/*
 * Frontend error reporting — the cross-SPA substrate every same-origin
 * Actionary browser surface reports failures through. Admin, portal,
 * and marketing all mount the same three captures:
 *
 *   - React render errors (caught by <FrontendErrorBoundary />)
 *   - Uncaught script errors (window "error" event)
 *   - Unhandled promise rejections ("unhandledrejection" event)
 *
 * Reports go to `/api/v1/platform/frontend-errors` — one endpoint, one
 * audit trail, one platform-admin triage view at
 * `/platform/frontend-errors`. The endpoint is auth-optional (portal and
 * marketing visitors hold no session at all, and a tenant-workspace
 * error can fire before the auth context resolves), rate-limited
 * server-side at 60/min/IP.
 *
 * The report URL is relative, so every consuming surface must be served
 * from an origin whose `/api/*` routes to core-api — the session cookie
 * then rides along and the browser never involves CORS. Admin, portal,
 * and marketing all satisfy this through their `deploy/Caddyfile` host
 * blocks.
 *
 * Best-effort: never throws, never blocks. If reporting itself fails the
 * error is dropped so error reporting can never create a loop.
 */

export type FrontendErrorSource =
  | "react_render"
  | "uncaught_error"
  | "unhandled_rejection"
  // A named action a person took that failed on a fault rather than on
  // their input — a read that 500s, a save that never reaches the API.
  // The surface shows them a message; the fault still belongs in the
  // triage queue. Input a person can correct is not this and stays out.
  | "handled_action";

export type FrontendErrorReport = {
  source: FrontendErrorSource;
  message: string;
  stack?: string | null;
  route?: string | null;
  user_agent?: string | null;
  build_id?: string | null;
  details?: Record<string, unknown> | null;
};

// Build identifier exposed at build time (Vite injects this when set in
// the deploy script). When absent we ship an empty string — the admin
// view tolerates missing build IDs.
const BUILD_ID =
  (import.meta as unknown as { env?: { VITE_BUILD_ID?: string } }).env
    ?.VITE_BUILD_ID ?? "";

// Coalesce identical errors fired in rapid bursts so a render-loop bug
// doesn't DDOS the report endpoint. 5-second window per message.
const recentlyReported = new Map<string, number>();
const DEDUP_WINDOW_MS = 5000;

function dedupKey(report: FrontendErrorReport): string {
  return `${report.source}::${report.message}::${report.route ?? ""}`;
}

function shouldSkip(report: FrontendErrorReport): boolean {
  const key = dedupKey(report);
  const last = recentlyReported.get(key) ?? 0;
  const now = Date.now();
  if (now - last < DEDUP_WINDOW_MS) return true;
  recentlyReported.set(key, now);
  // Cap the dedup table at 200 entries so a long-running session can't
  // grow unbounded. LRU-ish: drop the oldest when over the limit.
  if (recentlyReported.size > 200) {
    const oldest = [...recentlyReported.entries()].sort(
      ([, a], [, b]) => a - b,
    )[0]?.[0];
    if (oldest) recentlyReported.delete(oldest);
  }
  return false;
}

export function reportFrontendError(
  partial: Omit<FrontendErrorReport, "route" | "user_agent" | "build_id">,
): void {
  const report: FrontendErrorReport = {
    ...partial,
    route:
      typeof location !== "undefined"
        ? `${location.pathname}${location.search}`
        : null,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    build_id: BUILD_ID || null,
  };
  if (shouldSkip(report)) return;
  // Fire-and-forget. credentials:include lets the backend resolve the
  // user/tenant if a session cookie is present; failure to send is
  // swallowed silently — we don't want error-reporting to recurse.
  try {
    fetch("/api/v1/platform/frontend-errors", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(report),
      keepalive: true,
    }).catch(() => {
      // Reporting failed; nothing we can do.
    });
  } catch {
    // Synchronous throw — same swallow.
  }
}

let installed = false;

export function installFrontendErrorListeners(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event: ErrorEvent) => {
    const err = event.error;
    reportFrontendError({
      source: "uncaught_error",
      message:
        (err instanceof Error ? err.message : String(event.message)) ||
        "Uncaught error",
      stack: err instanceof Error ? err.stack ?? null : null,
      details: {
        filename: event.filename ?? null,
        line: event.lineno ?? null,
        col: event.colno ?? null,
      },
    });
  });

  window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      reportFrontendError({
        source: "unhandled_rejection",
        message:
          (reason instanceof Error ? reason.message : String(reason)) ||
          "Unhandled promise rejection",
        stack: reason instanceof Error ? reason.stack ?? null : null,
      });
    },
  );

  /*
   * A code-split chunk that failed to load.
   *
   * The bundler dispatches `vite:preloadError` on window when a dynamic
   * import cannot be fetched. It is a custom event, so the `error`
   * listener above never sees it, and the router catches its own import
   * failures, so the rejection never reaches `unhandledrejection`
   * either. Without this listener the most common post-release failure
   * — a tab holding a previous build asking for a chunk the current one
   * does not carry — reports nothing at all: the route simply never
   * mounts and the click appears to do nothing.
   *
   * Assets outlive the release that produced them, so this is the
   * residual case rather than the common one: a tab left open past the
   * retention window, asking for a chunk no recent release carried.
   * The report goes first, then the page reloads onto the current build
   * — a deliberate move forward, rather than a click that silently does
   * nothing. `keepalive` on the report is what lets it survive the
   * navigation.
   *
   * Reloading is guarded against repeating. If the reload lands on a
   * build that fails the same way, a second attempt would loop the tab
   * indefinitely, so the failure is reported and left visible instead.
   */
  window.addEventListener("vite:preloadError", (event: Event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    const url = (event as Event & { target?: unknown }).target;
    reportFrontendError({
      source: "uncaught_error",
      message:
        payload instanceof Error
          ? payload.message
          : "Failed to load an application chunk",
      stack: payload instanceof Error ? payload.stack ?? null : null,
      details: {
        kind: "chunk_load_failed",
        chunk: typeof url === "string" ? url : null,
      },
    });
    if (hasAlreadyReloadedForChunkFailure()) return;
    markReloadedForChunkFailure();
    window.location.reload();
  });
}

const CHUNK_RELOAD_KEY = "actionary.chunk_reload";
/** How long one reload suppresses another. Long enough to outlast a
 *  deploy, short enough that a later, unrelated failure still recovers. */
const CHUNK_RELOAD_WINDOW_MS = 10 * 60 * 1000;

function hasAlreadyReloadedForChunkFailure(): boolean {
  try {
    const at = window.sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!at) return false;
    return Date.now() - Number(at) < CHUNK_RELOAD_WINDOW_MS;
  } catch {
    // Storage unavailable (private mode, blocked cookies). Reloading
    // once is better than a route that never mounts, and without a
    // record the guard cannot be honoured — so decline to reload rather
    // than risk a loop.
    return true;
  }
}

function markReloadedForChunkFailure(): void {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // Nothing to do; the guard above already declined in this case.
  }
}
